import { type ComboboxProps } from '@payaca/ambrosia';
import {
  useController,
  useFormContext,
  type FieldPathByValue,
  type FieldValues,
} from 'react-hook-form';
import {
  useCustomerSearch,
  type CustomerSearchProps,
  type KnownCustomer,
} from './useCustomerSearch';

export type CustomerSearchFieldProps = CustomerSearchProps &
  Pick<ComboboxProps<number, false>, 'value' | 'onChange' | 'multiple'>;

/**
 * Binds a Combobox to a Customer id field. Spread the result onto the control,
 * inside a `Field` carrying the same `name`:
 *
 * ```tsx
 * const customerField = useCustomerSearchField<TFormValues, 'customerId'>({
 *   name: 'customerId',
 * });
 *
 * <Field name="customerId">
 *   <Field.Label>Customer</Field.Label>
 *   <Combobox {...customerField} />
 * </Field>
 * ```
 *
 * Reads the form from context, so it must be called inside the `Form`. This is
 * the one place react-hook-form and the Customer search meet: each is swapped
 * without touching the other.
 */
export const useCustomerSearchField = <
  TFieldValues extends FieldValues,
  TName extends FieldPathByValue<TFieldValues, number | null>,
>(params: {
  name: TName;
  knownCustomer?: KnownCustomer;
}): CustomerSearchFieldProps => {
  const { name, knownCustomer } = params;

  const { control } = useFormContext<TFieldValues>();
  const { field } = useController<TFieldValues, TName>({ name, control });

  const search = useCustomerSearch({
    selectedCustomerId: field.value ?? null,
    knownCustomer,
  });

  return {
    ...search,
    multiple: false,
    value: field.value ?? undefined,
    onChange: (customerId) => field.onChange(customerId ?? null),
  };
};
